import { IconChevronRight } from '@tabler/icons-react'

import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/ScrollArea'
import { cn } from '@/utils'

import { HierarchyNode } from '@/types'
import { areAllChildrenSelected } from './branchSelectUtil'

interface LevelColumnProps {
  title: string
  nodes: HierarchyNode[]
  selectedIds: Set<string>
  expandedId: string | null
  onSelect: (node: HierarchyNode, checked: boolean) => void
  onExpand: (node: HierarchyNode) => void
  isLeafLevel?: boolean
}

export const LevelColumn = ({
  title,
  nodes,
  selectedIds,
  expandedId,
  onSelect,
  onExpand,
  isLeafLevel = false,
}: LevelColumnProps) => {
  return (
    <div className="flex flex-col min-w-[180px] border-r border-gray-200 last:border-r-0">
      {/* Column Header */}
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
        <span className="text-sm font-medium text-gray-600">{title}</span>
      </div>

      {/* Column Content */}
      <ScrollArea className="flex-1 h-full">
        <div className="p-1">
          {nodes.map((node) => {
            const hasChildren = node.children && node.children.length > 0
            const isSelected = hasChildren
              ? areAllChildrenSelected(node, selectedIds)
              : selectedIds.has(String(node.id))
            const isExpanded = expandedId === String(node.id)

            return (
              <div
                key={node.id}
                className={cn(
                  'flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer hover:bg-gray-100 transition-colors my-1',
                  isExpanded && 'bg-primary/10 border border-primary/30'
                )}
              >
                {/* Checkbox and Label */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) =>
                      onSelect(node, checked as boolean)
                    }
                    className="flex-shrink-0"
                    onClick={(e) => {
                      // Prevent expanding when clicking directly on the checkbox
                      e.stopPropagation()
                    }}
                  />
                  <span
                    className="text-sm truncate"
                    onClick={(e) => {
                      // Clicking the label expands/collapses the node
                      e.stopPropagation()
                      if (!isLeafLevel && hasChildren) {
                        onExpand(node)
                      }
                    }}
                  >
                    {node.name}
                  </span>
                </div>

                {/* Expand Arrow */}
                {!isLeafLevel && hasChildren && (
                  <button
                    className={cn(
                      'p-1 rounded transition-colors flex-shrink-0'
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      onExpand(node)
                    }}
                  >
                    <IconChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            )
          })}

          {nodes.length === 0 && (
            <div className="px-2 py-4 text-sm text-gray-400 text-center">
              No items
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

export default LevelColumn
